import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { DAY_IDS, getWeekdayLabels } from './weekdays';
import { useT } from '@/contexts/I18nContext';
import type { ScheduleExceptionDraft, ScheduleGroup } from './workingHoursState';
import { type CardTarget } from './workingHoursState';
import { formatDateRu } from './workingHoursView';

export type ScheduleCardVariant = 'interval' | 'weekend';

const timeInputSx = {
  width: 'auto',
  minWidth: 58,
  px: 0,
  py: 0,
  height: 22,
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: 1.2,
  fontFamily: 'inherit',
  fontVariantNumeric: 'tabular-nums',
  border: 'none',
  background: 'transparent',
  outline: 'none',
  '&::-webkit-calendar-picker-indicator': { display: 'none' },
  '&::-webkit-inner-spin-button': { display: 'none' },
} as const;

const exceptionChipSx = {
  height: 26,
  fontSize: '0.8125rem',
  fontWeight: 500,
  cursor: 'grab',
  borderColor: 'rgba(25, 118, 210, 0.3)',
  bgcolor: 'rgba(25, 118, 210, 0.08)',
  color: 'text.primary',
} as const;

const deleteActionSx = {
  color: 'text.disabled',
  p: 0.25,
  opacity: 0.45,
  transition: 'opacity 0.15s',
} as const;

const dayChipSelected = {
  interval: { borderColor: 'transparent', bgcolor: 'primary.main', color: 'primary.contrastText' as const },
  weekend:  { borderColor: 'transparent', bgcolor: 'rgba(96, 125, 139, 0.9)', color: '#fff' as const },
} as const;

const dayChipUnselected = { borderColor: 'divider', bgcolor: 'transparent', color: 'text.secondary' as const };

function dayChipSx(selected: boolean, variant: ScheduleCardVariant) {
  return {
    height: 28,
    minWidth: 36,
    cursor: 'pointer',
    ...(selected ? dayChipSelected[variant] : dayChipUnselected),
    fontWeight: selected ? 600 : 500,
    fontSize: '0.8125rem',
    transition: 'background-color 0.15s, color 0.15s',
    '&:hover': { bgcolor: selected ? (variant === 'interval' ? 'primary.dark' : 'rgba(96, 125, 139, 1)') : 'action.hover' },
    '& .MuiChip-label': { px: 1.1 },
  } as const;
}

interface TimeInputProps {
  value: string;
  onChange: (time: string) => void;
  allDay: boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, allDay }) => (
  <Box sx={{
    display: 'inline-flex',
    alignItems: 'center',
    height: 30,
    borderRadius: 1.5,
    px: 0.75,
    transition: 'background-color 0.15s',
    bgcolor: allDay ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
    '&:hover': { bgcolor: allDay ? 'rgba(25, 118, 210, 0.06)' : 'action.hover' },
    '&:focus-within': { bgcolor: 'action.hover' },
  }}>
    <Box component="input" type="time"
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      disabled={allDay}
      sx={{
        ...timeInputSx,
        color: allDay ? 'text.disabled' : 'inherit',
        cursor: allDay ? 'not-allowed' : 'text',
        '&:disabled': {
          color: 'rgba(15, 60, 110, 0.82)',
          WebkitTextFillColor: 'rgba(15, 60, 110, 0.82)',
        },
      }}
    />
  </Box>
);

export interface ExceptionChipsProps {
  list: ScheduleExceptionDraft[];
  mt: number;
  onExceptionDragStart: (e: React.DragEvent<HTMLElement>, exceptionId: string) => void;
  onExceptionDrag: () => void;
  onExceptionDragEnd: () => void;
  onRemoveException: (localId: string) => void;
}

export const ExceptionChips: React.FC<ExceptionChipsProps> = ({
  list,
  mt,
  onExceptionDragStart,
  onExceptionDrag,
  onExceptionDragEnd,
  onRemoveException,
}) => {
  if (list.length === 0) return null;
  return (
    <Box sx={{ mt, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {list.map(ex => (
        <Chip
          key={ex.localId}
          draggable
          onDragStart={(e) => onExceptionDragStart(e, ex.localId)}
          onDrag={onExceptionDrag}
          onDragEnd={onExceptionDragEnd}
          label={formatDateRu(ex.date)}
          size="small"
          variant="outlined"
          onDelete={() => onRemoveException(ex.localId)}
          deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
          sx={exceptionChipSx}
        />
      ))}
    </Box>
  );
};

export interface ScheduleCardProps {
  target: CardTarget;
  dragTarget: CardTarget | 'new' | null;
  variant: ScheduleCardVariant;
  dayOwners: CardTarget[];
  exceptions: ScheduleExceptionDraft[];
  interval?: {
    group: ScheduleGroup;
    allDay: boolean;
    onOpenTimeChange: (time: string) => void;
    onCloseTimeChange: (time: string) => void;
    onToggleAllDay: () => void;
  };
  onAssignDay: (day: number, target: CardTarget) => void;
  onDeleteCard: (target: CardTarget) => void;
  onExceptionDragStart: (e: React.DragEvent<HTMLElement>, exceptionId: string) => void;
  onExceptionDrag: () => void;
  onExceptionDragEnd: () => void;
  onRemoveException: (localId: string) => void;
  onBeginDrop: (e: React.DragEvent<HTMLElement>, target: CardTarget | 'new') => void;
  onClearDrop: () => void;
  onDropToPeriod: (e: React.DragEvent<HTMLElement>, target: CardTarget) => void;
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  target,
  dragTarget,
  variant,
  dayOwners,
  exceptions,
  interval,
  onAssignDay,
  onDeleteCard,
  onExceptionDragStart,
  onExceptionDrag,
  onExceptionDragEnd,
  onRemoveException,
  onBeginDrop,
  onClearDrop,
  onDropToPeriod,
}) => {
  const { lang } = useT();
  const labels = getWeekdayLabels(lang);
  const weekendCaption = lang === 'en' ? 'Closed days' : 'Выходные';
  return (
  <Box
    onDragOver={(e) => onBeginDrop(e, target)}
    onDragLeave={onClearDrop}
    onDrop={(e) => onDropToPeriod(e, target)}
    sx={{
      border: '1px solid',
      borderStyle: variant === 'weekend' ? 'dashed' : 'solid',
      borderColor: dragTarget === target ? 'primary.main' : 'divider',
      borderRadius: 3,
      p: variant === 'interval' ? '14px 16px 16px' : undefined,
      px: variant === 'weekend' ? 2 : undefined,
      pt: variant === 'weekend' ? 1.25 : undefined,
      pb: variant === 'weekend' ? 1.75 : undefined,
      position: 'relative',
      transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
      bgcolor: dragTarget === target ? 'action.hover' : 'transparent',
      '&:hover .interval-action': { opacity: 1 },
    }}
  >
    {variant === 'weekend' && (
      <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.68rem', color: 'text.secondary' }}>
        {weekendCaption}
      </Typography>
    )}

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {DAY_IDS.map(day => (
        <Chip
          key={`${target}-${day}`}
          label={labels[day]}
          size="small"
          variant="outlined"
          onClick={() => onAssignDay(day, target)}
          sx={dayChipSx(dayOwners[day] === target, variant)}
        />
      ))}
    </Box>

    <ExceptionChips
      list={exceptions}
      mt={0.55}
      onExceptionDragStart={onExceptionDragStart}
      onExceptionDrag={onExceptionDrag}
      onExceptionDragEnd={onExceptionDragEnd}
      onRemoveException={onRemoveException}
    />

    {variant === 'interval' && interval && (
      <Box sx={{ mt: exceptions.length > 0 ? 1.1 : 1.35, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, pr: 2.5 }}>
        <TimeInput value={interval.group.openTime} onChange={interval.onOpenTimeChange} allDay={interval.allDay} />
        <Typography variant="body2" color="text.disabled" sx={{ mx: -0.15 }}>—</Typography>
        <TimeInput value={interval.group.closeTime} onChange={interval.onCloseTimeChange} allDay={interval.allDay} />

        <IconButton
          size="small"
          onClick={interval.onToggleAllDay}
          sx={{
            color: interval.allDay ? 'primary.main' : 'text.disabled',
            width: 30,
            height: 30,
            p: 0,
            borderRadius: 1.5,
            bgcolor: interval.allDay ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
            '&:hover': { bgcolor: interval.allDay ? 'rgba(25, 118, 210, 0.12)' : 'action.hover', color: interval.allDay ? 'primary.main' : 'text.secondary' },
          }}
          title="24 часа"
        >
          <AllInclusiveIcon sx={{ fontSize: 16 }} />
        </IconButton>

      </Box>
    )}

    <IconButton
      className="interval-action"
      size="small"
      onClick={() => onDeleteCard(target)}
      sx={{
        ...deleteActionSx,
        position: 'absolute',
        right: 8,
        top: 8,
        '&:hover': { opacity: 1, color: 'error.main', bgcolor: 'rgba(211, 47, 47, 0.08)' },
      }}
    >
      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
  );
};

export default ScheduleCard;
